import { useEffect, useId, useState } from "react";
import LoadField from "./LoadField";
import EntryHintInfo from "./EntryHintInfo";
import SetGuidanceRow from "./SetGuidanceRow";
import {
  defaultUnitForSet,
  isSetPerformed,
  loadPresentation,
} from "../domain/executionUi";

function SecondsField({ value, placeholder, setIndex = null, disabled, compact = false, onCommit }) {
  const [draft, setDraft] = useState(value ?? "");
  const [error, setError] = useState("");
  const errorId = useId();
  useEffect(() => setDraft(value ?? ""), [value]);

  function commit() {
    if (draft === "") {
      setError("");
      // Passive blur of an empty field is not a commit.
      if (value != null) onCommit(null);
      return;
    }
    const parsed = Number(draft);
    if (!Number.isInteger(parsed) || parsed < 1 || parsed > 3600) {
      setError("Seconds must be from 1 to 3600.");
      return;
    }
    setError("");
    setDraft(parsed);
    // An unchanged value is not a commit (§9.4).
    if (parsed !== value) onCommit(parsed);
  }

  function updateDraft(nextDraft) {
    if (!/^\d*$/.test(nextDraft)) return;
    setError("");
    setDraft(nextDraft);
  }

  return (
    <label className="lz-workout-number-field">
      <span className={compact ? "lz-v2-visually-hidden" : "lz-workout-field-label"}>Seconds</span>
      <input
        aria-label={setIndex == null ? "Seconds" : `Set ${setIndex} seconds`}
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        maxLength={4}
        value={draft}
        placeholder={placeholder || "--"}
        disabled={disabled}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? errorId : undefined}
        onChange={(event) => updateDraft(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => { if (event.key === "Enter") event.currentTarget.blur(); }}
      />
      {error ? <span id={errorId} className="lz-workout-field-error" role="alert">{error}</span> : null}
    </label>
  );
}

export default function TimedSetRow({
  item,
  set,
  prescribed,
  profile,
  preferences,
  disabled,
  onPatch,
  onRemove,
  compact = false,
  ordinal = null,
  unitMemory = null,
  // This set's mapped LAST (§13.10). Timed values are never LAST-seeded in V1.
  last = null,
}) {
  // The visible number is the row's current 1-based place in the item.
  // `setIndex` stays the persisted structural index (never renumbered, so
  // it keeps gaps after a removed set) and is not shown to the athlete.
  const setNumber = ordinal ?? set.setIndex;
  const performed = isSetPerformed(profile, set);
  const hint = loadPresentation(profile, preferences, set.load).info;
  return (
    <div
      className={`lz-workout-set-row${compact ? " lz-workout-set-row--compact" : ""}${performed ? " is-performed" : ""}`}
      data-set-index={set.setIndex}
      data-set-ordinal={setNumber}
      data-performed={performed ? "true" : "false"}
    >
      <span className="lz-workout-set-number">
        {compact ? (
          <span className="lz-workout-set-state" aria-hidden="true">
            {performed ? "✓" : setNumber}
          </span>
        ) : null}
        {compact ? <span className="lz-v2-visually-hidden">Set </span> : null}
        {compact && performed ? <span>{setNumber}</span> : !compact ? setNumber : null}
        {performed ? <span className="lz-v2-visually-hidden"> completed</span> : null}
      </span>
      <SecondsField
        value={set.seconds}
        placeholder={prescribed?.targetSeconds ? String(prescribed.targetSeconds) : ""}
        setIndex={setNumber}
        disabled={disabled}
        compact={compact}
        onCommit={(seconds) => onPatch({ seconds })}
      />
      <LoadField
        profile={profile}
        load={set.load}
        defaultUnit={defaultUnitForSet(item, set, preferences, unitMemory)}
        preferences={preferences}
        setIndex={setNumber}
        disabled={disabled}
        compact={compact}
        onCommit={(load) => onPatch({ load })}
      />
      {set.origin === "ADDED" ? (
        <button type="button" className="lz-workout-icon-button" disabled={disabled} onClick={onRemove} aria-label={`Remove set ${setNumber}`}>×</button>
      ) : null}
      {!compact ? <EntryHintInfo>{hint}</EntryHintInfo> : null}
      <SetGuidanceRow measurementType="TIMED_HOLD" last={last} />
    </div>
  );
}

export { SecondsField };
